/**
 * The plugin state is a discriminated union representing the lifecycle
 * of an AI inline completion suggestion.
 *
 * Trigger mode:
 *   • 'manual' — user presses Cmd+Shift+Space anywhere in the document.
 *     Suggestion appears as inline ghost text at the cursor. Accepted with Tab.
 *
 * State transitions:
 *   idle
 *     → TRIGGER_MANUAL   (Cmd+Shift+Space pressed)           → loading (immediate, no debounce)
 *   loading
 *     → SET_SUGGESTION   (API responded)                       → suggestion
 *     → DISMISS          (error / Escape / blur)               → idle
 *   suggestion
 *     → accept command   (Tab; inserts text + DISMISS)        → idle
 *     → DISMISS          (Escape / blur / doc changed)        → idle
 */

export type SuggestionTrigger = 'manual';

export type AiAutocompletePluginState =
	| { status: 'idle' }
	| {
			/** For manual: absolute cursor position in the doc when triggered. */
			cursorPos: number;
			status: 'loading';
			trigger: SuggestionTrigger;
	  }
	| {
			/** For manual: absolute cursor position in the doc when triggered. */
			cursorPos: number;
			status: 'suggestion';
			suggestion: string;
			trigger: SuggestionTrigger;
	  };

// ---------------------------------------------------------------------------
// Actions dispatched via tr.setMeta(aiAutocompletePluginKey, action).
// Accept does insert + DISMISS in the command; apply() and appendTransaction handle the rest.
// ---------------------------------------------------------------------------

export const ACTIONS = {
	TRIGGER_MANUAL: 'TRIGGER_MANUAL',
	SET_SUGGESTION: 'SET_SUGGESTION',
	DISMISS: 'DISMISS',
} as const;

export type AiAutocompletePluginAction =
	| {
			cursorPos: number;
			type: typeof ACTIONS.TRIGGER_MANUAL;
	  }
	| { suggestion: string; type: typeof ACTIONS.SET_SUGGESTION }
	| { type: typeof ACTIONS.DISMISS };
